import { useQuery } from '@apollo/client';
import { cx } from '@orn/shared-web/utils/cx';
import * as React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import { TOOLS } from '../../tools';
import { ADMIN_LIST_USERS } from '../admin/AdminSelectUser/AdminSelectUser';

import { useCommandPalette } from './CommandPaletteProvider';
import { buildPaletteSections, isUserLookupQuery } from './paletteSections';

import type { IPaletteItem } from './paletteSections';
import type {
  AdminListUsersQuery,
  AdminListUsersQueryVariables,
} from '../admin/AdminSelectUser/__generated__/AdminSelectUser.gql';

/**
 * Global keyboard-first switcher. Mounted once at the top of `MainApp`,
 * listens at the window level for Cmd+K (or Ctrl+K on non-mac), shows a
 * centered overlay of matching rows, and navigates on Enter. Its open state
 * lives in `CommandPaletteProvider`, so the header's search box opens it too.
 * A query reaches three kinds of row (see `buildPaletteSections`):
 *
 *   - an id, whole or as its eight-character short form — opens the Object
 *     inspector on it; recognized by shape, never searched
 *   - a tool — matched on its name + keywords
 *   - a user — up to `MAX_USER_MATCHES` users whose email contains the query
 *     once it holds an `@`, or whose phone number holds its digits once it
 *     reads as a number; opens their household in Accounts
 *
 * Keyboard:
 *   - Cmd+K / Ctrl+K   — toggle open (also closes if already open)
 *   - ArrowDown / ↓    — move selection down
 *   - ArrowUp   / ↑    — move selection up
 *   - Enter             — open the selected row
 *   - Esc               — close without navigating
 */
const CommandPalette = () => {
  const { isOpen, setIsOpen } = useCommandPalette();
  const [query, setQuery] = React.useState('');
  const [selectedIndex, setSelectedIndex] = React.useState(0);
  const inputRef = React.useRef<HTMLInputElement>(null);
  const listRef = React.useRef<HTMLUListElement>(null);
  const navigate = useNavigate();
  const location = useLocation();

  // Shares the user pickers' cache, but still refetches so an account created
  // since a picker last read the list turns up.
  const isUserLookup = isOpen && isUserLookupQuery(query);
  const {
    data: usersData,
    loading: usersLoading,
    error: usersError,
  } = useQuery<AdminListUsersQuery, AdminListUsersQueryVariables>(ADMIN_LIST_USERS, {
    skip: !isUserLookup,
    fetchPolicy: 'cache-and-network',
  });

  const sections = React.useMemo(
    () => buildPaletteSections(query, TOOLS, usersData?.adminListUsers ?? []),
    [query, usersData]
  );
  const items = React.useMemo(() => sections.flatMap((section) => section.items), [sections]);

  // Global toggle: Cmd+K / Ctrl+K anywhere in the app.
  React.useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setIsOpen((prev) => !prev);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [setIsOpen]);

  // Reset query + selection whenever the palette opens; close whenever the
  // route changes (user navigated via Enter, keyboard shortcut, or otherwise).
  React.useEffect(() => {
    if (isOpen) {
      setQuery('');
      setSelectedIndex(0);
      // Wait a tick for the input to mount.
      void Promise.resolve().then(() => inputRef.current?.focus());
    }
  }, [isOpen]);

  React.useEffect(() => {
    setIsOpen(false);
  }, [location.pathname, setIsOpen]);

  // Clamp selection when the list shrinks.
  React.useEffect(() => {
    if (selectedIndex >= items.length) {
      setSelectedIndex(Math.max(0, items.length - 1));
    }
  }, [items.length, selectedIndex]);

  const handleSelect = React.useCallback(
    (item: IPaletteItem) => {
      setIsOpen(false);
      void navigate(item.href);
    },
    [navigate, setIsOpen]
  );

  // Keyboard moves only: scrolling on hover would slide the next row under a
  // still cursor, selecting it and scrolling again, all the way down the list.
  const moveSelection = (step: 1 | -1) => {
    if (items.length === 0) {
      return;
    }
    // Stops at either end rather than wrapping, like the j / k lists.
    const next = Math.min(Math.max(selectedIndex + step, 0), items.length - 1);
    setSelectedIndex(next);
    // `nearest` would leave the first row's section header scrolled off.
    if (next === 0) {
      listRef.current?.scrollTo({ top: 0 });
    } else {
      listRef.current?.querySelector(`#cmd-option-${next}`)?.scrollIntoView({ block: 'nearest' });
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Escape') {
      e.preventDefault();
      setIsOpen(false);
      return;
    }
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      e.preventDefault();
      moveSelection(e.key === 'ArrowDown' ? 1 : -1);
      return;
    }
    if (e.key === 'Enter') {
      e.preventDefault();
      const item = items[selectedIndex];
      if (item) {
        handleSelect(item);
      }
    }
  };

  if (!isOpen) {
    return null;
  }

  const activeDescendantId = items[selectedIndex] ? `cmd-option-${selectedIndex}` : undefined;
  const hasUserLookupError = isUserLookup && usersError !== undefined;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Command palette"
      className="bg-bg-overlay/40 fixed inset-0 z-50 flex items-start justify-center pt-24 backdrop-blur-sm"
      onClick={(e) => {
        if (e.target === e.currentTarget) {
          setIsOpen(false);
        }
      }}
    >
      <div className="border-border-primary bg-bg-primary flex w-full max-w-xl flex-col border shadow-lg">
        <div className="border-border-primary border-b">
          <input
            ref={inputRef}
            type="text"
            value={query}
            onChange={(e) => {
              setQuery(e.target.value);
              // A new query re-ranks the list, so select and show the top match — the one Enter opens.
              setSelectedIndex(0);
              listRef.current?.scrollTo({ top: 0 });
            }}
            onKeyDown={handleKeyDown}
            placeholder="Jump to a tool, email, phone, or id…"
            aria-label="Search tools, user emails and phone numbers, and ids"
            aria-controls="cmd-options"
            aria-activedescendant={activeDescendantId}
            className="text-fg-primary placeholder:text-fg-quaternary w-full border-0 bg-transparent px-4 py-3 font-mono text-sm outline-none"
          />
        </div>
        <ul
          ref={listRef}
          id="cmd-options"
          className="max-h-80 overflow-y-auto py-1"
          role="listbox"
          aria-label="Matches"
        >
          {items.length === 0 ? (
            <li
              className={cx(
                'px-4 py-3 text-xs',
                hasUserLookupError ? 'text-fg-error-primary' : 'text-fg-tertiary italic'
              )}
            >
              {emptyStateMessage(hasUserLookupError, isUserLookup && usersLoading)}
            </li>
          ) : (
            sections.map((section) => (
              <li key={section.label} role="presentation">
                <div className="text-fg-quaternary px-4 pb-1 pt-2 font-mono text-[10px] uppercase tracking-wide">
                  {section.label}
                  {section.matchCount > section.items.length && ` · ${section.items.length} of ${section.matchCount}`}
                </div>
                <ul role="group" aria-label={section.label}>
                  {section.items.map((item) => {
                    const index = items.indexOf(item);
                    const Icon = item.icon;
                    const isSelected = index === selectedIndex;
                    return (
                      <li
                        key={item.key}
                        id={`cmd-option-${index}`}
                        role="option"
                        aria-selected={isSelected}
                        onMouseEnter={() => setSelectedIndex(index)}
                        onClick={() => handleSelect(item)}
                        className={cx(
                          'flex cursor-pointer items-center gap-3 px-4 py-2 text-sm',
                          isSelected ? 'bg-bg-secondary' : 'hover:bg-bg-secondary'
                        )}
                      >
                        <Icon
                          className={cx(
                            'h-6 w-6 shrink-0 transition-colors',
                            isSelected ? 'text-fg-brand-primary' : 'text-fg-secondary'
                          )}
                        />
                        <span className="flex min-w-0 flex-1 flex-col">
                          <span className={cx('text-fg-primary truncate font-medium', item.isTitleMono && 'font-mono')}>
                            {item.title}
                          </span>
                          <span className="text-fg-tertiary truncate text-xs">{item.subtitle}</span>
                        </span>
                        <span className="text-fg-quaternary shrink-0 font-mono text-xs">{item.hint}</span>
                      </li>
                    );
                  })}
                </ul>
              </li>
            ))
          )}
        </ul>
        <div className="border-border-primary text-fg-quaternary flex items-center gap-4 border-t px-4 py-2 font-mono text-[10px] uppercase tracking-wide">
          <span>↑↓ navigate</span>
          <span>↵ open</span>
          <span>esc close</span>
        </div>
      </div>
    </div>
  );
};

/** A failed user lookup is told apart from an empty one, so a missing row never reads as a missing user. */
function emptyStateMessage(hasUserLookupError: boolean, isSearchingUsers: boolean): string {
  if (hasUserLookupError) {
    return 'The user lookup failed.';
  }
  return isSearchingUsers ? 'Searching users…' : 'Nothing matches.';
}

export default CommandPalette;
